export function safeJsonParse(value, fallback = []) {
  try {
    let parsed = value;
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
      // If still a string, parse again (double-encoded)
      if (typeof parsed === "string") {
        parsed = JSON.parse(parsed);
      }
    }
    return parsed;
  } catch (e) {
    return fallback;
  }
}

export function formatFileSize(sizeInBytes) {
  if (sizeInBytes === 0) return "0 Bytes";
  const kb = 1024;
  const mb = kb * 1024;
  const gb = mb * 1024;

  if (sizeInBytes >= gb) {
    return (sizeInBytes / gb).toFixed(2) + " GB";
  } else if (sizeInBytes >= mb) {
    return (sizeInBytes / mb).toFixed(2) + " MB";
  } else if (sizeInBytes >= kb) {
    return (sizeInBytes / kb).toFixed(2) + " KB";
  } else {
    return sizeInBytes + " Bytes";
  }
}

export function getValueUtilitie(compareKey, value, array, type) {
  const match = array.find(
    (x) => x[compareKey]?.toString() === value?.toString(),
  );
  return match ? match[type] : "";
}

export function getAccessToken() {
  return ""; //localStorage.getItem('bearer_token') || null;
}

export const formatCurrentWorkExp = (workExp) => {
  try {
    const parsed = safeJsonParse(workExp, []);

    if (!Array.isArray(parsed) || parsed.length === 0) return null;

    const currentJob =
      parsed.find((job) => job.isCurrent === true) || parsed[0];

    const company = currentJob.organization || "";
    const role = currentJob.jobTitle || "";

    const start = formatMonthYear(currentJob.startDate);
    const end = currentJob.isCurrent
      ? "Present"
      : formatMonthYear(currentJob.endDate);

    const period = start || end ? `${start} - ${end}` : "";

    return {
      company,
      companyPeriod: period,
      role,
    };
  } catch (error) {
    return null;
  }
};

export const formatEducation = (educationDetails) => {
  try {
    const parsed = safeJsonParse(educationDetails, []);

    if (!Array.isArray(parsed) || parsed.length === 0) return null;

    const first = parsed[0];

    const educationName =
      first.degree || first.education || first.organization || "";

    const start = formatMonthYear(first.startDate);
    const end = first.isCurrent ? "Present" : formatMonthYear(first.endDate);

    const period = start || end ? `${start} - ${end}` : "";

    return {
      education: educationName,
      educationPeriod: period,
    };
  } catch (error) {
    return null;
  }
};

const formatMonthYear = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  if (isNaN(date)) return "";

  return date.toLocaleString("en-US", {
    month: "short",
    year: "numeric",
  });
};

export const normalizeValue = (val) => {
  if (!val) return "";
  if (Array.isArray(val)) return val[0]?.toString().toLowerCase() || "";
  return val.toString().toLowerCase();
};

export const parseSkills = (skillsString) => {
  return skillsString
    ?.split(/[\n,]+/)
    .map((s) => s.trim())
    .filter((s) => s);
};
export function truncateWords(text, wordCount = 3, charLimit = 25) {
  if (!text) return "";
  const words = text.split(" ");
  if (words.length > wordCount) {
    return words.slice(0, wordCount).join(" ") + "...";
  }
  if (text.length > charLimit) {
    return text.substring(0, charLimit) + "...";
  }
  return text;
}


export const getFileExtension = (docUrl) => {
  if (!docUrl) return undefined;

  const cleanUrl = docUrl.split("?")[0];
  return cleanUrl.split(".").pop()?.toLowerCase();
};

export const openDocumentInNewTab = (docData, fileExtension) => {
  const newTab = window.open("", "_blank");
  if (!newTab) return;
  
  let ext = (fileExtension || "").toLowerCase();
  
  // Map common MIME types to extensions
  const mimeToExt = {
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.ms-powerpoint": "ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "text/plain": "txt",
    "text/csv": "csv",
    "application/rtf": "rtf"
  };

  if (mimeToExt[ext]) {
    ext = mimeToExt[ext];
  } else if (ext.includes("/")) {
    // If it's still an unmapped MIME type, fallback to parsing the URL
    ext = (getFileExtension(docData) || "").toLowerCase();
  } else if (!ext) {
    ext = (getFileExtension(docData) || "").toLowerCase();
  }

  if (ext === "pdf" || ["jpg", "jpeg", "png", "gif", "txt", "csv"].includes(ext)) {
    newTab.document.write("<html><head><title>Document Preview</title></head><body style='margin:0; display:flex; justify-content:center; align-items:center; height:100vh; font-family:sans-serif;'><h2>Loading preview...</h2></body></html>");
    newTab.document.close();

    fetch(docData, { cache: "no-store", credentials: "omit" })
      .then(res => res.blob())
      .then(blob => {
        let mimeType = blob.type;
        if (ext === "pdf") mimeType = "application/pdf";
        else if (ext === "jpg" || ext === "jpeg") mimeType = "image/jpeg";
        else if (ext === "png") mimeType = "image/png";
        else if (ext === "gif") mimeType = "image/gif";
        else if (ext === "txt") mimeType = "text/plain";
        else if (ext === "csv") mimeType = "text/csv";
        
        const typedBlob = new Blob([blob], { type: mimeType });
        const url = URL.createObjectURL(typedBlob);
        
        newTab.location.replace(url);
      })
      .catch(err => {
        console.error("Failed to fetch preview blob", err);
        newTab.location.href = docData;
      });
  } else if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)) {
    newTab.location.href = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(docData)}`;
  } else {
    newTab.location.href = `https://docs.google.com/gview?url=${encodeURIComponent(docData)}&embedded=true&t=${Date.now()}`;
  }
};

export const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date);
};